import React, { useState } from 'react';
import { Check, X, Box, Plane } from 'lucide-react';

type ProblemSolutionFRProps = {
  variant?: 'agents' | 'partenaires';
  activeProfileId?: string;
  onProfileChange?: (id: string) => void;
};

const profiles = [
  {
    id: 'matterport-only',
    shortTitle: 'Matterport seul',
    fullTitle: "J'ai une visite Matterport",
    icon: Box,
    emoji: '📐',
    challenges: [
      'Votre Matterport est en ligne, mais les prospects décrochent avant la cuisine',
      'Faire appel à un vidéaste ? Comptez 3 jours et une facture salée',
      'Filmer vous-même demande du temps et le résultat manque de professionnalisme',
    ],
    solutions: [
      'Votre annonce est vivante. La vidéo se lance automatiquement. Parfait pour les réseaux',
      "Vous envoyez votre lien. 24h plus tard, c'est prêt",
      'Votre client arrive à la visite déjà convaincu',
    ],
    challengesPartners: [
      'Vous n\'avez aucun moyen simple de proposer la vidéo sur chaque prestation',
      'Vos clients veulent de la vidéo, mais pas au prix d\'une production cinématographique',
      'Produire une vidéo demande du temps, du matériel et des compétences que vous n\'avez pas forcément',
    ],
    solutionsPartners: [
      'Proposez la vidéo sur chaque prestation, et vendez plus de Matterport',
      'Recevez une vidéo professionnelle, avec ou sans branding client',
      'Transmettez votre lien Matterport, on s\'occupe de tout',
    ],
  },
  {
    id: 'matterport-drone',
    shortTitle: 'Matterport + drone',
    fullTitle: "J'ai Matterport + séquences drone",
    icon: Plane,
    emoji: '🚁',
    challenges: [
      "Vidéos de drone d'un côté. Matterport de l'autre. Deux livrables, aucune cohérence",
      "L'acheteur zappe entre deux contenus et ne voit jamais le bien en entier",
      "Assembler tout ça vous-même demande du temps et des logiciels que vous n'avez pas forcément",
    ],
    solutions: [
      'Une seule vidéo. Du ciel jusqu\'au salon',
      "Votre annonce raconte une histoire complète, l'acheteur reste jusqu'au bout",
      "Envoyez votre lien + vos clips drone. 24h plus tard, c'est prêt",
    ],
    challengesPartners: [
      'Vous livrez les fichiers bruts, quelqu\'un d\'autre finit le travail',
      'Vous n\'avez pas le temps ni les logiciels pour assembler tout ça',
      'Votre client s\'attend à une vidéo complète, Matterport et drone réunis dans un seul montage',
    ],
    solutionsPartners: [
      'Proposez un pack Matterport + drone + vidéo, une prestation complète sans post-production',
      'Envoyez le lien Matterport et vos clips drone, on s\'occupe du montage.',
      'Recevez une vidéo complète et professionnelle, avec ou sans branding client.',
    ],
  },
];

const getProfileButtonLabel = (profileId: string, variant: 'agents' | 'partenaires') => {
  if (profileId === 'matterport-only') return 'Matterport';
  return 'Matterport + clips de drone';
};

const ProblemSolutionFR: React.FC<ProblemSolutionFRProps> = ({ variant = 'agents', activeProfileId, onProfileChange }) => {
  const [internalActiveProfile, setInternalActiveProfile] = useState('matterport-only');
  const activeProfile = activeProfileId || internalActiveProfile;
  const currentProfile = profiles.find((p) => p.id === activeProfile);
  const isPartners = variant === 'partenaires';

  const handleProfileClick = (id: string) => {
    if (!activeProfileId) {
      setInternalActiveProfile(id);
    }
    if (onProfileChange) {
      onProfileChange(id);
    }
  };

  if (!isPartners) {
    return (
      <section className="py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              Ce que ça change pour vous
            </h2>
          </div>

          <p className="text-sm text-slate-500 font-medium mb-3 text-center">Vous avez déjà :</p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12 px-2 sm:px-0">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleProfileClick(profile.id)}
                className={`btn-premium overflow-hidden flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-full font-medium transition-all duration-300 text-sm ${
                  activeProfile === profile.id
                    ? 'bg-[#3C59B2] text-white shadow-lg scale-105'
                    : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'
                }`}
              >
                <profile.icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="sm:hidden">{getProfileButtonLabel(profile.id, 'agents')}</span>
                <span className="hidden sm:inline">{getProfileButtonLabel(profile.id, 'agents')}</span>
              </button>
            ))}
          </div>

          {currentProfile && (
            <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-6 sm:p-8 lg:p-10 bg-red-50/50 md:border-r border-b md:border-b-0 border-gray-100">
                  <h4 className="font-manrope font-bold text-red-600 text-base sm:text-lg mb-4 text-center">Sans 3DtoVideo</h4>
                  <div className="space-y-2 sm:space-y-3">
                    {currentProfile.challenges.map((challenge, index) => (
                      <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                        <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-gray-700">{challenge}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-6 sm:p-8 lg:p-10 bg-green-50/50">
                  <h4 className="font-manrope font-bold text-green-700 text-base sm:text-lg mb-4 text-center">Avec 3DtoVideo</h4>
                  <div className="space-y-2 sm:space-y-3">
                    {currentProfile.solutions.map((solution, index) => (
                      <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-gray-700">{solution}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
            Ce que ça change pour vous
          </h2>
        </div>

        <p className="text-sm text-slate-500 font-medium mb-3 text-center">Vous proposez déjà :</p>

        <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12 px-2 sm:px-0">
          {profiles.map((profile) => (
            <button
              key={profile.id}
              onClick={() => handleProfileClick(profile.id)}
              className={`btn-premium overflow-hidden flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-full font-medium transition-all duration-300 text-sm ${
                activeProfile === profile.id
                  ? 'bg-[#3C59B2] text-white shadow-lg scale-105'
                  : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'
              }`}
            >
              <profile.icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="sm:hidden">{getProfileButtonLabel(profile.id, 'partenaires')}</span>
              <span className="hidden sm:inline">{getProfileButtonLabel(profile.id, 'partenaires')}</span>
            </button>
          ))}
        </div>

        {currentProfile && (
          <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-6 sm:p-8 lg:p-10 bg-red-50/50 md:border-r border-b md:border-b-0 border-gray-100">
                <h4 className="font-manrope font-bold text-red-600 text-base sm:text-lg mb-4 text-center">Sans 3DtoVideo</h4>
                <div className="space-y-2 sm:space-y-3">
                  {(currentProfile.challengesPartners || currentProfile.challenges).map((challenge, index) => (
                    <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                      <X className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700">{challenge}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-6 sm:p-8 lg:p-10 bg-green-50/50">
                <h4 className="font-manrope font-bold text-green-700 text-base sm:text-lg mb-4 text-center">Avec 3DtoVideo</h4>
                <div className="space-y-2 sm:space-y-3">
                  {currentProfile.solutionsPartners.map((solution, index) => (
                    <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700">{solution}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProblemSolutionFR;
